import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryForm from "@/components/EnquiryForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    FileText, DownloadSimple, MagnifyingGlass, Books, Notepad, ArrowRight,
    GraduationCap, Certificate, FunnelSimple, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaperExplainer from "@/components/PaperExplainer";

const SITE_ORIGIN = "https://wibpro.com";

const PAPER_TYPES = [
    { id: "past_paper",   label: "Past papers",   icon: FileText },
    { id: "sample_paper", label: "Sample papers", icon: Certificate },
    { id: "topic_wise",   label: "Topic-wise",    icon: Books },
    { id: "notes",        label: "Notes",         icon: Notepad },
];

const PAPER_LABEL = Object.fromEntries(PAPER_TYPES.map(p => [p.id, p.label]));

export default function ResourcesPage() {
    const { subjectCode: routeSubject } = useParams();
    const navigate = useNavigate();

    const [subjects, setSubjects] = useState([]);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [board, setBoard] = useState("all");        // all | IGCSE | A-Level
    const [subject, setSubject] = useState(routeSubject || "all");
    const [paperType, setPaperType] = useState("all");
    const [q, setQ] = useState("");
    const [explaining, setExplaining] = useState(null);   // resource being explained

    useEffect(() => {
        Promise.all([
            api.get("/resources/subjects").then(r => r.data),
            api.get("/resources").then(r => r.data),
        ]).then(([s, r]) => { setSubjects(s); setResources(r); })
          .catch(() => toast.error("Could not load resources"))
          .finally(() => setLoading(false));
    }, []);

    // When route param changes (e.g. clicking a chip that navigates), sync it
    // into the subject filter and auto-set the board.
    useEffect(() => {
        if (routeSubject && subjects.length) {
            const s = subjects.find(x => x.code === routeSubject);
            if (s) {
                setSubject(s.code);
                setBoard(s.board);
            }
        } else if (!routeSubject) {
            setSubject("all");
        }
    }, [routeSubject, subjects]);

    // ---- SEO metadata ----
    const activeSubject = subject !== "all" ? subjects.find(s => s.code === subject) : null;
    const seo = useMemo(() => {
        if (activeSubject) {
            const s = activeSubject;
            const count = resources.filter(r => r.subject_code === s.code).length;
            const title = `${s.board} ${s.name} ${s.code} — Past Papers, Sample Papers & Notes | Wibpro Academy`;
            const description = `Free ${s.board} ${s.name} (${s.code}) past papers, sample papers, topic-wise question sets and important notes. ${count > 0 ? `${count} papers available. ` : ""}Download without signup at Wibpro Academy.`;
            const canonical = `${SITE_ORIGIN}/resources/${s.code}`;
            return { title, description, canonical };
        }
        return {
            title: "Free Past Papers — IGCSE & AS/A Level Cambridge | Wibpro Academy",
            description: "Download free Cambridge IGCSE and AS/A Level past papers, sample papers, topic-wise questions and notes. 19 subjects incl. Maths 0580 & 9709, Physics 0625 & 9702, Accounting 0452 & 9706. No signup required.",
            canonical: `${SITE_ORIGIN}/resources`,
        };
    }, [activeSubject, resources]);

    const jsonLd = useMemo(() => {
        if (activeSubject) {
            return {
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                "name": `${activeSubject.board} ${activeSubject.name} ${activeSubject.code} Past Papers`,
                "description": seo.description,
                "url": seo.canonical,
                "about": {
                    "@type": "Course",
                    "name": `${activeSubject.board} ${activeSubject.name}`,
                    "courseCode": activeSubject.code,
                    "educationalCredentialAwarded": activeSubject.board,
                    "provider": { "@type": "Organization", "name": "Cambridge Assessment International Education" },
                },
                "publisher": {
                    "@type": "EducationalOrganization",
                    "name": "Wibpro Academy",
                    "url": SITE_ORIGIN,
                },
            };
        }
        return {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "IGCSE & AS/A Level Past Papers",
            "description": seo.description,
            "url": seo.canonical,
            "publisher": {
                "@type": "EducationalOrganization",
                "name": "Wibpro Academy",
                "url": SITE_ORIGIN,
            },
        };
    }, [activeSubject, seo]);

    const boards = useMemo(() => Array.from(new Set(subjects.map(s => s.board))), [subjects]);

    const visibleSubjects = useMemo(() =>
        board === "all" ? subjects : subjects.filter(s => s.board === board),
        [subjects, board]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return resources.filter(r => {
            if (board !== "all" && r.subject_board !== board) return false;
            if (subject !== "all" && r.subject_code !== subject) return false;
            if (paperType !== "all" && r.paper_type !== paperType) return false;
            if (needle) {
                const hay = `${r.title} ${r.subject_name} ${r.subject_code} ${r.year || ""} ${r.session || ""}`.toLowerCase();
                if (!hay.includes(needle)) return false;
            }
            return true;
        });
    }, [resources, board, subject, paperType, q]);

    // Group filtered results by subject → paper_type for structure
    const grouped = useMemo(() => {
        const g = {};
        for (const r of filtered) {
            const key = `${r.subject_code} · ${r.subject_board} ${r.subject_name}`;
            g[key] = g[key] || { subject_code: r.subject_code, subject_board: r.subject_board, subject_name: r.subject_name, items: [] };
            g[key].items.push(r);
        }
        return Object.values(g).sort((a, b) => a.subject_code.localeCompare(b.subject_code));
    }, [filtered]);

    const downloadHref = (id) => `${api.defaults.baseURL}/resources/${id}/download`;

    // Wrap subject state change to also update URL (deep-linkable + SEO)
    const changeSubject = (code) => {
        setSubject(code);
        if (code === "all") navigate("/resources", { replace: true });
        else navigate(`/resources/${code}`, { replace: true });
    };

    // Counts per subject for chip pills
    const countBySubject = useMemo(() => {
        const c = {};
        for (const r of resources) c[r.subject_code] = (c[r.subject_code] || 0) + 1;
        return c;
    }, [resources]);

    return (
        <div className="min-h-screen paper-grain" data-testid="resources-page">
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <link rel="canonical" href={seo.canonical} />
                {/* OpenGraph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={seo.title} />
                <meta property="og:description" content={seo.description} />
                <meta property="og:url" content={seo.canonical} />
                <meta property="og:site_name" content="Wibpro Academy" />
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seo.title} />
                <meta name="twitter:description" content={seo.description} />
                {/* JSON-LD structured data */}
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>
            <Navbar />

            {/* Hero */}
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-14">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-ink/60 mb-5">
                            <span className="w-8 h-px bg-gold"></span>
                            <span>Library · IGCSE · AS &amp; A Level</span>
                        </div>
                        <h1 className="font-serif font-black text-5xl md:text-7xl leading-[0.95] text-ink">
                            The paper room.
                            <br />
                            <span className="italic font-light text-ink/80">Every past paper. Free.</span>
                        </h1>
                        <p className="mt-8 text-lg md:text-xl text-ink/70 max-w-2xl leading-relaxed">
                            Past papers, sample papers, topic-wise question sets and important notes for
                            Cambridge IGCSE and Cambridge International AS &amp; A Level. Filter by subject
                            code, download in one click — no account required.
                        </p>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="border-l-2 border-gold pl-5">
                            <div className="text-xs uppercase tracking-widest text-ink/50 mb-1">In the library</div>
                            <div className="font-serif text-4xl text-ink">{resources.length}</div>
                            <div className="text-sm text-ink/60">papers across {subjects.length} subjects</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="border-y border-ink/10 bg-parchment/40 sticky top-[76px] z-30 backdrop-blur-sm">
                <div className="mx-auto max-w-7xl px-6 py-4 grid md:grid-cols-12 gap-3 items-center">
                    <div className="md:col-span-1 hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-ink/60">
                        <FunnelSimple size={14} weight="duotone"/> Filter
                    </div>

                    <div className="md:col-span-3">
                        <Select value={board} onValueChange={(v) => { setBoard(v); changeSubject("all"); }}>
                            <SelectTrigger className="rounded-sm bg-bone" data-testid="filter-board"><SelectValue placeholder="Board"/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All boards</SelectItem>
                                {boards.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="md:col-span-3">
                        <Select value={subject} onValueChange={changeSubject}>
                            <SelectTrigger className="rounded-sm bg-bone" data-testid="filter-subject"><SelectValue placeholder="Subject"/></SelectTrigger>
                            <SelectContent className="max-h-80">
                                <SelectItem value="all">All subjects</SelectItem>
                                {visibleSubjects.map(s => (
                                    <SelectItem key={s.code} value={s.code}>
                                        {s.code} · {s.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="md:col-span-2">
                        <Select value={paperType} onValueChange={setPaperType}>
                            <SelectTrigger className="rounded-sm bg-bone" data-testid="filter-type"><SelectValue placeholder="Type"/></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All types</SelectItem>
                                {PAPER_TYPES.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="md:col-span-3 relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search title, year, session…"
                            className="rounded-sm bg-bone pl-9"
                            data-testid="filter-search"
                        />
                    </div>
                </div>
            </section>

            {/* Subject chips shortcut */}
            {board !== "all" && (
                <section className="mx-auto max-w-7xl px-6 mt-8">
                    <div className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-3">{board} — subject shortcuts</div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => changeSubject("all")}
                            data-testid="chip-all"
                            className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${
                                subject === "all" ? "bg-ink text-bone border-ink" : "bg-bone text-ink border-ink/20 hover:border-ink/50"
                            }`}
                        >All</button>
                        {visibleSubjects.map(s => (
                            <button
                                key={s.code}
                                onClick={() => changeSubject(s.code)}
                                data-testid={`chip-subject-${s.code}`}
                                className={`px-3 py-1.5 text-sm rounded-sm border transition-colors flex items-center gap-2 ${
                                    subject === s.code ? "bg-ink text-bone border-ink" : "bg-bone text-ink border-ink/20 hover:border-ink/50"
                                }`}
                            >
                                <span className="font-mono text-xs opacity-70">{s.code}</span>
                                <span>{s.name}</span>
                                {countBySubject[s.code] > 0 && (
                                    <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-sm ${subject === s.code ? "bg-bone/20 text-bone" : "bg-gold/20 text-ink"}`}>
                                        {countBySubject[s.code]}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* Results */}
            <section className="mx-auto max-w-7xl px-6 py-14">
                {loading && (
                    <div className="grid place-items-center py-24 text-ink/50">Loading library…</div>
                )}

                {!loading && filtered.length === 0 && (
                    <div className="border border-dashed border-ink/20 rounded-sm p-16 text-center bg-bone" data-testid="resources-empty">
                        <Books size={44} weight="duotone" className="mx-auto text-ink/40 mb-4" />
                        <div className="font-serif text-2xl text-ink">Nothing here yet.</div>
                        <p className="text-ink/60 mt-2 max-w-md mx-auto">
                            {resources.length === 0
                                ? "Our teachers are curating papers for these subjects right now — check back soon, or WhatsApp us and we\u2019ll send them to you directly."
                                : "No papers match those filters. Try a different subject, type or search."}
                        </p>
                        {resources.length === 0 && (
                            <a href="https://wa.me/919100560075" target="_blank" rel="noopener noreferrer">
                                <Button className="mt-6 bg-[#25D366] hover:bg-[#128C7E] rounded-sm text-white" data-testid="btn-empty-whatsapp">
                                    Request a paper via WhatsApp <ArrowRight size={16} className="ml-2"/>
                                </Button>
                            </a>
                        )}
                    </div>
                )}

                {!loading && grouped.map((group) => (
                    <div key={group.subject_code} className="mb-14" data-testid={`group-${group.subject_code}`}>
                        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5 border-b border-ink/10 pb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-gold">{group.subject_board}</div>
                                <div className="font-serif text-2xl md:text-3xl text-ink flex items-baseline gap-3">
                                    {group.subject_name}
                                    <span className="font-mono text-sm text-ink/50">{group.subject_code}</span>
                                </div>
                            </div>
                            <div className="text-xs text-ink/50">{group.items.length} paper{group.items.length === 1 ? "" : "s"}</div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {group.items.map((r) => {
                                const TypeIcon = PAPER_TYPES.find(t => t.id === r.paper_type)?.icon || FileText;
                                return (
                                    <div
                                        key={r.id}
                                        className="group p-5 border border-ink/10 bg-bone rounded-sm hover:border-ink/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200 flex flex-col"
                                        data-testid={`resource-card-${r.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <TypeIcon size={22} weight="duotone" className="text-ink shrink-0"/>
                                            <span className="text-[10px] uppercase tracking-widest text-gold-dark border border-gold/40 rounded-sm px-2 py-0.5 whitespace-nowrap">
                                                {PAPER_LABEL[r.paper_type] || r.paper_type}
                                            </span>
                                        </div>
                                        <div className="mt-4 font-serif text-lg text-ink leading-snug">{r.title}</div>
                                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink/60">
                                            {r.year && <span>Year <span className="font-mono">{r.year}</span></span>}
                                            {r.session && <span>· {r.session}</span>}
                                            {r.paper_number && <span>· Paper {r.paper_number}</span>}
                                        </div>
                                        {r.notes && (
                                            <p className="mt-3 text-xs text-ink/60 leading-relaxed line-clamp-2">{r.notes}</p>
                                        )}
                                        <div className="mt-auto pt-4 flex items-center justify-between text-xs">
                                            <span className="text-ink/40">{Math.round((r.size || 0) / 1024)} KB · {r.downloads || 0} downloads</span>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-ink/10 flex items-center gap-2">
                                            <button
                                                onClick={() => setExplaining(r)}
                                                data-testid={`btn-explain-${r.id}`}
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-sm bg-ink text-bone text-xs font-medium hover:bg-ink/90 transition-colors"
                                            >
                                                <Sparkle size={13} weight="fill" className="text-gold-light"/>
                                                AI hint
                                            </button>
                                            <a
                                                href={downloadHref(r.id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`btn-open-${r.id}`}
                                                className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-sm border border-ink/20 text-ink text-xs font-medium hover:border-ink/50 transition-colors"
                                            >
                                                <DownloadSimple size={13} weight="bold"/> Open
                                            </a>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </section>

            {/* CTA · enquiry */}
            <section className="border-t border-ink/10 bg-parchment/40">
                <div className="mx-auto max-w-7xl px-6 py-16 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Can&apos;t find something?</div>
                        <h2 className="font-serif text-4xl text-ink leading-tight">Request a paper, or ask about a subject.</h2>
                        <p className="mt-4 text-ink/70 leading-relaxed">
                            We add papers on demand &mdash; if you need a specific year, session or exam board
                            we don&apos;t have yet, tell us and we&apos;ll upload it within 48 hours.
                        </p>
                        <div className="mt-8 border-l-2 border-gold pl-5">
                            <div className="font-serif text-xl text-ink flex items-center gap-2">
                                <GraduationCap size={22} weight="duotone"/> Also enrol
                            </div>
                            <p className="text-sm text-ink/70 mt-2">
                                Wibpro Academy runs AS &amp; A Level and IGCSE tuitions in Hyderabad
                                and online. <Link to="/tuitions" className="underline underline-offset-4 decoration-gold decoration-2">See programs →</Link>
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <EnquiryForm source="resources" interestPlaceholder="e.g. IGCSE Maths 0580, May/June 2024"/>
                    </div>
                </div>
            </section>

            <Footer />

            {/* AI paper explainer sheet */}
            <PaperExplainer
                resource={explaining}
                open={!!explaining}
                onClose={() => setExplaining(null)}
            />
        </div>
    );
}
